/* eslint-disable no-plusplus */

import moment from "moment";
import { parseApiDateTime } from "src/utils/parseApiDateTime";

/* eslint-disable no-bitwise */
export const femaleAvatarList = [
    "https://api.dicebear.com/7.x/micah/svg?seed=alex&mouth=smile",
    "https://api.dicebear.com/7.x/micah/svg?seed=sophie&mouth=smile",
    "https://api.dicebear.com/7.x/micah/svg?seed=emily&mouth=smile",
    "https://api.dicebear.com/7.x/micah/svg?seed=zara&mouth=smile"
]

export const maleAvatarList = [
    "https://api.dicebear.com/7.x/micah/svg?seed=mark&mouth=smile",
    "https://api.dicebear.com/7.x/micah/svg?seed=daniel&mouth=smile",
    "https://api.dicebear.com/7.x/micah/svg?seed=ryan&mouth=smile",
    "https://api.dicebear.com/7.x/micah/svg?seed=james&mouth=smile"
]

export function hashString(str) {
    let hash = 0;
    for (let i = 0; i < (str?.length ?? 0); i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
        hash |= 0;
    }
    return Math.abs(hash);
}

export function formatTimeOrDateOnly(dateString, fallback) {
    try {
        // Strip fractional seconds and trailing Z — the backend sends naive UK-local
        // timestamps with a literal 'Z' that is NOT UTC. Parsing without the Z suffix
        // lets moment treat the value as already-local, avoiding a +1h BST double-offset.
        const cleanDate = dateString.replace(/(\.\d+)?Z$/i, '');
        const parsed = moment(cleanDate, "DD-MM-YYYY HH:mm:ss", true).toDate();

        if (Number.isNaN(parsed.getTime())) {
            throw new Error("Invalid date format");
        }

        const now = new Date();
        const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const todayEnd = new Date(todayStart.getTime() + 24 * 60 * 60 * 1000 - 1);
        const local = new Date(parsed.getTime());

        if (local >= todayStart && local <= todayEnd) {
            return local.toLocaleTimeString('en-US', {
                hour: 'numeric',
                minute: 'numeric',
                hour12: true,
            });
        }

        return `${String(local.getDate()).padStart(2, '0')}/${String(local.getMonth() + 1).padStart(2, '0')}/${local.getFullYear()}`;
    } catch (error) {
        console.error("error in date time format", error);
        return fallback ?? ""
    }
}

export function fetchContactDetails(contact) {
    const lastThread = contact?.last_message;
    const lastMessage = lastThread ? lastThread?.body : "";
    const updateTime = lastThread ? formatTimeOrDateOnly(lastThread?.updated_at, lastThread?.updated_at_human) : null;
    const isLastMessageReceived = lastThread ? lastThread?.from_waid === contact?.to_waid : false;
    let leadName = `${contact?.lead?.first_name ?? ''} ${contact?.lead?.last_name ?? ''}`;
    // const salutation = contact?.lead?.salutation ?? "mrs";
    leadName = leadName?.trim();
    const unreadMessageCount = contact?.is_read ? 0 : (contact?.unread_count ?? 0);
    const isRegisteredNumber = contact?.lead?.id ?? false;
    const markedAsUnread = !contact?.is_read;
    const isMediaMessage = lastThread?.whatsapp_message_media?.whatsapp_media_mime_type;
    const phoneNumber = contact?.to_waid;
    // F5: Guard against names that contain no alphanumeric characters — e.g. a
    // sole "." from a WhatsApp contact with no display name. Such strings would
    // render literally as "." in the sidebar row and produce "." as the avatar
    // initial. Fall back to the phone number for both display and initials so
    // the operator sees something useful instead of punctuation.
    const hasAlphanumeric = (s) => s && /[a-z0-9]/i.test(s);
    const finalName = (hasAlphanumeric(leadName) && leadName)
        || (hasAlphanumeric(contact?.name?.trim()) && contact?.name?.trim())
        || phoneNumber
        || '';
    const isCloseObject = {
        isClosed: contact?.is_closed,
        closedReason: contact?.metadata?.close_reason
    }
    let preview = "";
    if (lastThread) {
        const msgText = lastMessage || lastThread?.whatsapp_message_media?.whatsapp_media_caption || "";
        if (isLastMessageReceived) {
            preview = msgText;
        } else {
            preview = msgText ? `You: ${msgText}` : "";
        }
    }
    // Avatar: supply first two space-separated tokens so stringAvatar can
    // extract two initials. When finalName is a phone number (no spaces),
    // stringAvatar will render just the first char — acceptable fallback.
    // const avatarIndex = hashString(finalName || phoneNumber);
    // const avatar = salutation?.toLowerCase()?.includes("mrs") ? femaleAvatarList[avatarIndex % femaleAvatarList.length] : maleAvatarList[avatarIndex % maleAvatarList.length];
    const avatar = `${finalName?.split(" ")?.[0] ?? ""} ${finalName?.split(" ")?.[1] ?? ""}`
    return {
        avatar, name: finalName, preview, time: updateTime, isMediaMessage, unreadMessageCount, isRegisteredNumber, markedAsUnread, phoneNumber, isCloseObject
    }
}

export const arePhoneNumsEqual = (num1, num2) => {
    const firstPhoneNum = `${num1 ?? ''}`;
    const secondPhoneNum = `${num2 ?? ''}`;
    return firstPhoneNum?.slice(-10) === secondPhoneNum?.slice(-10);
}

export function formatUKDate(dateString) {
    // Strip fractional seconds and trailing Z — the backend sends naive UK-local
    // timestamps with a literal 'Z' that is NOT UTC. Parsing without the Z suffix
    // lets moment treat the value as already-local, avoiding a +1h BST double-offset.
    const parseDate = dateString.replace(/(\.\d+)?Z$/i, '');
    const ukTime = moment(parseDate, "MM-DD-YYYY HH:mm:ss", true).toDate();

    // Check if the parsed date is valid
    if (Number.isNaN(ukTime.getTime())) {
        throw new Error('Invalid date format');
    }

    // Get the current local time and the time offset in minutes
    const now = new Date();

    // Adjust the UK time by the local offset to get the local time
    const localTime = new Date(ukTime.getTime());

    // Define the start and end of today in local time
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const todayEnd = new Date(todayStart.getTime() + 24 * 60 * 60 * 1000 - 1);

    // Define the format options for today and not today
    const optionsToday = {
        hour: 'numeric',
        minute: 'numeric',
        hour12: true
    };

    const optionsNotToday = {
        day: '2-digit',
        month: 'short',
        hour: 'numeric',
        minute: 'numeric',
        hour12: true
    };

    // Format and return the date string based on whether it is today or not
    if (localTime >= todayStart && localTime <= todayEnd) {
        return `Today, ${localTime.toLocaleString('en-US', optionsToday)}`;
    }
    return localTime.toLocaleString('en-US', optionsNotToday);

}

// #27 (extracted from ChatPage for testability) — F9: interleave call events
// into the conversation timeline. `messages` is newest-first
// (ordering=-created_time, rendered column-reverse), so each call is inserted
// before the first message that is OLDER than it. Attached system rows
// (addMediaButtons, emitted immediately before their bubble) are kept glued
// to their bubble by backing the insertion index up past them. Events are
// only merged when they belong to the open thread — eventsThreadId guards
// against stale rows during thread switch. MUTATES `messages` in place and
// returns it.
export function mergeCallEventsIntoMessages(messages, callEvents, { activeThreadId, eventsThreadId } = {}) {
    if (
        !Array.isArray(messages) ||
        !activeThreadId ||
        String(eventsThreadId) !== String(activeThreadId) ||
        !Array.isArray(callEvents) ||
        callEvents.length === 0
    ) return messages;

    const isAttachedSystem = (m) => m?.type === 'system' && m?.specialType === 'addMediaButtons';

    // C5: guarantee messages is newest-first before splicing; callers may pass an
    // array that hasn't been sorted yet (e.g. optimistic inserts). Sort in-place
    // to match the existing mutate-and-return contract.
    //
    // Three categories of undated row need special treatment:
    //   addMediaButtons — glued to their bubble; inherit the bubble's timestamp so
    //     the stable sort keeps the pair together (equal ts + original order).
    //   banner rows (windowAlert, templateRequired, plain freeze system rows) — no
    //     timestamp, injected via unshift at the top; must stay at their original
    //     index and are excluded from the sort entirely.
    //
    // Algorithm:
    //   1. Walk messages once, separating pinned (undated, non-anchor) from sortable.
    //   2. Stamp sortable items: addMediaButtons inherit the next sibling's timestamp.
    //   3. Sort sortable newest-first (stable sort preserves addMediaButtons gluing).
    //   4. Re-insert each pinned item at its original index into the sorted result.
    //   5. Write the rebuilt array back into messages in-place.
    const pinned = []; // { m, origIdx }
    const sortable = [];
    messages.forEach((m, idx) => {
        if (parseWhatsappTimestamp(m?.createdAt) === null && !isAttachedSystem(m)) {
            pinned.push({ m, origIdx: idx });
        } else {
            sortable.push(m);
        }
    });
    const stampedSortable = sortable.map((m) => {
        let ts = parseWhatsappTimestamp(m?.createdAt)?.valueOf() ?? null;
        if (ts === null && isAttachedSystem(m)) {
            // Bubble is the original next sibling; messages is not yet mutated here.
            const origIdx = messages.indexOf(m);
            ts = parseWhatsappTimestamp(messages[origIdx + 1]?.createdAt)?.valueOf() ?? 0;
        }
        return { m, ts: ts ?? 0 };
    });
    stampedSortable.sort((a, b) => b.ts - a.ts);
    const rebuilt = stampedSortable.map(({ m }) => m);
    // Re-insert pinned rows at their original positions (ascending order preserves
    // relative ordering among multiple consecutive banners).
    pinned.forEach(({ m, origIdx }) => {
        rebuilt.splice(Math.min(origIdx, rebuilt.length), 0, m);
    });
    rebuilt.forEach((m, idx) => { messages[idx] = m; });
    callEvents
        .map((call) => ({ call, time: parseWhatsappTimestamp(call?.initiated_at || call?.created_at) }))
        .filter((entry) => !!entry.time)
        // Newest first so each splice lands relative to already-placed events.
        .sort((a, b) => b.time.valueOf() - a.time.valueOf())
        .forEach(({ call, time }) => {
            // Duration only when the call was actually answered and ended.
            let durationSeconds = null;
            const answeredMoment = parseWhatsappTimestamp(call?.answered_at);
            const endedMoment = parseWhatsappTimestamp(call?.ended_at);
            if (answeredMoment && endedMoment) {
                const secs = Math.round((endedMoment.valueOf() - answeredMoment.valueOf()) / 1000);
                if (secs >= 0) durationSeconds = secs;
            }
            const eventObj = {
                id: `call-${call?.id}`,
                type: 'callEvent',
                direction: call?.direction,
                callStatus: call?.status,
                // Server-computed — authoritative, never re-derived client-side.
                isMissed: !!call?.is_missed,
                durationSeconds,
                timestamp: parsedDateTimeForWhatsapp(call?.initiated_at || call?.created_at, ''),
                createdAt: call?.initiated_at || call?.created_at,
            };
            let insertAt = messages.length;
            for (let i = 0; i < messages.length; i += 1) {
                const mTime = messages[i]?.createdAt ? parseWhatsappTimestamp(messages[i].createdAt) : null;
                if (mTime && mTime.valueOf() <= time.valueOf()) {
                    insertAt = i > 0 && isAttachedSystem(messages[i - 1]) ? i - 1 : i;
                    break;
                }
            }
            messages.splice(insertAt, 0, eventObj);
        });
    return messages;
}

// #27 — extract a thread id from a call/notification payload where `thread`
// may be a bare id, an object ({ id } or { thread_id }), or absent. Single
// source of truth — previously duplicated in LaunchpadMessages and
// SearchLeadForWhatsapp.
export function readThreadId(value) {
    if (!value) return null;
    if (typeof value === 'object') return value.id ?? value.thread_id ?? null;
    return value;
}

// F9 — parse a backend WhatsApp timestamp into a moment for SORTING (not
// display). The wire format is "DD-MM-YYYY HH:mm:ss.SSSSSSZ" — a naive
// UK-local time with a literal (non-UTC) Z, same quirk formatUKDate handles.
// Falls back to strict ISO-8601 for any endpoint that emits standard dates.
// Returns null when unparseable so callers can skip the row instead of
// sorting on an Invalid Date.
export function parseWhatsappTimestamp(raw) {
    if (!raw) return null;
    const cleaned = String(raw).replace(/(\.\d+)?Z$/i, '');
    const strict = moment(cleaned, 'DD-MM-YYYY HH:mm:ss', true);
    if (strict.isValid()) return strict;
    const iso = moment(raw, moment.ISO_8601, true);
    return iso.isValid() ? iso : null;
}

export function parsedDateTimeForWhatsapp(inputDate, fallbackDate, returnRawDateTime = false) {
    try {
        const date = inputDate?.split(' ')?.[0]?.split('-');
        const time = inputDate?.split(' ')?.[1];
        const newDateTimeString = `${date?.[1]}-${date?.[0]}-${date?.[2]} ${time}`;
        if (returnRawDateTime)
            return newDateTimeString;
        return formatUKDate(newDateTimeString);
    } catch (error) {
        console.error("Error in parsing date time");
        return fallbackDate;
    }
}

// HTML-entity encode every character that could break out of HTML text or
// attribute context. Used to defend against XSS — inbound WhatsApp message
// bodies are user-supplied and the rendered HTML output of parseWhatsappMarkup
// flows into a span via __html, so every literal byte from `text` MUST be
// entity-encoded before any markup transform fires.
const escapeHtml = (s) => String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

export const parseWhatsappMarkup = (text) => {
    if (!text) return '';

    // Split into tokens where placeholders like {{1}} are isolated. Both the
    // token interior AND the surrounding literal text are treated as
    // UNTRUSTED — patient-inbound and operator-typed messages can both flow
    // through this function, and either path can contain a `{{...}}` token
    // whose contents must not be allowed to inject HTML.
    const tokens = text.split(/({{.*?}})/g);

    return tokens
        .map((token) => {
            // Placeholder token (e.g. {{lead_first_name}} or {{1}}). Render
            // as the literal text `{{...}}` — never as HTML. Escape the
            // interior so a malicious body like `{{<img src=x onerror=...>}}`
            // can't inject a tag.
            if (token.startsWith("{{") && token.endsWith("}}")) {
                const body = token.slice(2, -2);
                return `{{${escapeHtml(body)}}}`;
            }

            // Step 1: entity-encode the entire literal segment FIRST. This
            // guarantees the input contains no live `<`, `>`, `&`, `"`, `'`
            // before any markup transform fires, so patient text like
            // `<script>alert(1)</script>` becomes inert
            // `&lt;script&gt;alert(1)&lt;/script&gt;` text.
            // URLs are lifted out BEFORE any markup runs and put back
            // last. A Stripe checkout URL contains `cs_live_...`, and the
            // italic rule turned that into `cs<i>live</i>...` -- after which
            // the linkifier's `[^\s<]+` stopped dead at the `<`, so the
            // anchor ended at "cs" and the rest of the link was plain text a
            // patient could not click. On the one message they have to act
            // on. Real WhatsApp does not apply markup inside a URL either.
            //
            // Holding them aside also keeps the tel: rule off the digits in
            // a URL, which was the same bug waiting its turn.
            // U+E000 is a Private Use Area code point, not a control
            // character -- eslint's no-control-regex rejected \u0000
            // -- and it cannot occur in a patient's message. Stripped
            // from the input first so nothing can forge a placeholder.
            const escaped = escapeHtml(token).replace(/\uE000/g, '');
            const heldUrls = [];
            const held = escaped.replace(/(https?:\/\/[^\s<]+)/g, (url) => {
                heldUrls.push(url);
                return `\uE000U${heldUrls.length - 1}\uE000`;
            });

            return held
                // Markdown-like formatting. Operates on already-escaped
                // text — the inserted `<b>`, `<i>`, etc. are the only raw
                // HTML this function emits. The `*`, `_`, `~`, backtick
                // markers themselves are ASCII and untouched by escapeHtml.
                .replace(/\*\*(.*?)\*\*/g, "<b>$1</b>")
                .replace(/\*(.*?)\*/g, "<b>$1</b>")
                .replace(/_(.*?)_/g, "<i>$1</i>")
                .replace(/~(.*?)~/g, "<s>$1</s>")
                .replace(/```(.*?)```/gs, "<code>$1</code>")
                .replace(/\n/g, "<br>")
                // Convert phone numbers (10+ digits) to tel: links. Phone
                // numbers contain only digits and a small set of ASCII
                // punctuation that is safe inside `href="tel:…"`, but we
                // still escape defensively.
                .replace(/(\+?\d[\d\s\-().]{9,}\d)/g, (_, num) => {
                    const safeNum = escapeHtml(num);
                    return `<a href="tel:${safeNum}">${safeNum}</a>`;
                })
                // The URLs go back LAST, as anchors. They were escaped with
                // the rest of the token before being held, so re-escaping
                // here would double them -- an `&` in a query string came
                // back as `&amp;amp;` under the old order.
                .replace(/\uE000U(\d+)\uE000/g, (_, index) => {
                    const safeUrl = heldUrls[Number(index)];
                    return `<a href="${safeUrl}" target="_blank" rel="noopener noreferrer">${safeUrl}</a>`;
                });
        })
        .join('');
};

// Single source of truth for "the most recent inbound message in this
// thread's message list" — used by both ChatPage's 24h-window gate (which
// disables free-text send / shows "Use template or copy") and BookingPanel's
// send-mode check, so the two never disagree about whether the window is
// open. `messages` is the raw `whatsappChats` array from the whatsappV2
// slice (objects with a `from_waid` field, NOT the transformed `{ type:
// 'received' }` shape ChatPage builds for rendering); `phoneNumber` is
// `whatsAppChatMetaData.phoneNumber` / `whatsAppChatMetaData.thread.to_waid`
// — the patient's number for the CURRENTLY open thread. Messages are matched
// by `from_waid` against that number (the same "received" test ChatPage
// uses via `arePhoneNumsEqual`), so a stale list from a different thread
// (whatsappChats hasn't been cleared yet on thread switch) won't be
// misread as this thread's inbound message.
//
// Returns the RAW `created_at` string (never a reformatted display value —
// `isWithin24Hours` below expects the untouched API timestamp) of the most
// recent inbound message, or `null` when there is none.
export function getLastInboundMessageCreatedAt(messages, phoneNumber) {
    if (!Array.isArray(messages) || !phoneNumber) return null;
    let latestCreatedAt = null;
    let latestInstant = null;
    messages.forEach((message) => {
        if (!arePhoneNumsEqual(message?.from_waid, phoneNumber)) return;
        const createdAt = message?.created_at;
        if (!createdAt) return;
        const parsed = parseApiDateTime(createdAt);
        if (!parsed) return;
        if (!latestInstant || parsed.isAfter(latestInstant)) {
            latestCreatedAt = createdAt;
            latestInstant = parsed;
        }
    });
    return latestCreatedAt;
}

// WhatsApp 24h customer-service window check. `inputDateTime` MUST be the raw
// API timestamp of the lead's last inbound message (BE shape:
// `DD-MM-YYYY HH:mm:ss[.ffffff]Z`, where the trailing "Z" is a misleading
// literal, NOT UTC — it's actually Europe/London wall-clock time). Using
// `moment.utc()` on that string (the previous implementation) treated UK-local
// time as UTC, shifting the window by 1 hour during BST. `parseApiDateTime`
// resolves the correct absolute instant (BST/GMT-safe), so `.diff()` against
// `moment()` (also an instant) is timezone-independent.
export const isWithin24Hours = inputDateTime => {
    try {
        const lastInbound = parseApiDateTime(inputDateTime);
        if (!lastInbound) return false;
        const targetTime = lastInbound.clone().add(24, 'hours');
        const difference = targetTime.diff(moment());
        if (difference <= 0) {
            return false;
        }
        return true;
    } catch (error) {
        console.error("errror in timer", error);
        return false;
    }
}

// Server-aware service-window resolver. The backend now exposes
// ``service_window_open``, ``service_window_closes_at`` and ``last_inbound_at``
// on the thread serialiser. Prefer those when present; fall back to the
// legacy client-side computation off the last received bubble so the UI
// keeps working against an old API.
//
// Returns: { open: bool, closesAt: Date|null, msRemaining: number|null }
export const resolveServiceWindow = (thread, fallbackInboundIso) => {
    if (thread && typeof thread.service_window_open === 'boolean') {
        const closesAt = thread.service_window_closes_at
            ? new Date(thread.service_window_closes_at)
            : null;
        const msRemaining = closesAt
            ? Math.max(closesAt.getTime() - Date.now(), 0)
            : null;
        return {
            open: thread.service_window_open && (msRemaining === null || msRemaining > 0),
            closesAt,
            msRemaining,
        };
    }
    // Legacy fallback — derive from the last received bubble's timestamp.
    if (!fallbackInboundIso) {
        return { open: false, closesAt: null, msRemaining: null };
    }
    const open = isWithin24Hours(fallbackInboundIso);
    return { open, closesAt: null, msRemaining: null };
};

// Format the milliseconds-remaining value as "5h 12m" / "47m" / "30s" so
// the operator sees how much window they have left at a glance.
export const formatWindowRemaining = (msRemaining) => {
    if (!msRemaining || msRemaining <= 0) return null;
    const totalSec = Math.floor(msRemaining / 1000);
    const hours = Math.floor(totalSec / 3600);
    const minutes = Math.floor((totalSec % 3600) / 60);
    if (hours > 0) return `${hours}h ${minutes}m`;
    if (minutes > 0) return `${minutes}m`;
    return `${totalSec}s`;
};

// Fixed palette used for deterministic avatar colors
const AVATAR_COLORS = [
    // primary
    '#00A76F', '#007867', '#004B50',
    // secondary
    '#8E33FF', '#5119B7', '#27097A',
    // info
    '#00B8D9', '#006C9C', '#003768',
    // success
    '#22C55E', '#118D57', '#065E49',
    // warning
    '#FFAB00', '#B76E00', '#7A4100',
    // error
    '#FF5630', '#B71D18', '#7A0916',
    // greys (accent neutrals)
    '#637381', '#454F5B', '#212B36'
];

function stringToColor(string, phoneNumber) {
    const key = (string && String(string).trim()) || (phoneNumber && String(phoneNumber).trim()) || '';
    // Fallback to a primary color if we somehow get an empty key
    if (!key) return AVATAR_COLORS[0];

    const hash = hashString(key);
    const idx = Math.abs(hash) % AVATAR_COLORS.length;
    return AVATAR_COLORS[idx];
}

export function stringAvatar(name, phoneNumber, styleObject) {
    return {
        sx: {
            bgcolor: stringToColor(name, phoneNumber),
            ...(styleObject ?? {})
        },
        children: `${name?.split(' ')?.[0]?.[0]?.toUpperCase() ?? ""}${name?.split(' ')?.[1]?.[0]?.toUpperCase() || ""}`,
    };
}